import React from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'

function TagList({ value }) {
  const items = (value ?? '').split(',').map((item) => item.trim()).filter(Boolean)
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {items.map((item) => <span key={item} className="bg-background rounded-full px-2.5 py-0.5 text-xs font-medium text-dark-blue">{item}</span>)}
    </div>
  )
}

function Card({ title, children }) {
  return (
    <div className="w-full rounded-2xl border border-border-lines p-4">
      <p className="text-xs font-semibold text-secondary tracking-wide uppercase mb-3">{title}</p>
      <div className="flex flex-col gap-4">{children}</div>
    </div>
  )
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2.5">
      {Icon && <Icon size={16} className="text-secondary shrink-0 mt-0.5" />}
      <div className="min-w-0 flex-1">
        <p className="text-xs text-secondary">{label}</p>
        <div className="text-sm text-dark-blue mt-0.5 break-words">{children}</div>
      </div>
    </div>
  )
}

function SplitRow({ icon: Icon, leftLabel, leftValue, rightLabel, rightValue }) {
  return (
    <div className="flex items-start gap-2.5">
      {Icon && <Icon size={16} className="text-secondary shrink-0 mt-0.5" />}
      <div className="flex-1 grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs text-secondary">{leftLabel}</p>
          <p className="text-sm text-dark-blue mt-0.5">{leftValue}</p>
        </div>
        <div>
          <p className="text-xs text-secondary">{rightLabel}</p>
          <p className="text-sm text-dark-blue mt-0.5">{rightValue}</p>
        </div>
      </div>
    </div>
  )
}

export default withTranslation()(function CreativeRepeatTab({ t, campaign }) {
  const f = t('rewardAds.detail.creative_repeat', { returnObjects: true })
  const { scheduleSummary } = campaign

  const frequencyCount = parseInt(scheduleSummary.frequency, 10)
  const deliveryNote = Number.isFinite(frequencyCount)
    ? `Ad will be shown up to ${frequencyCount} times per user per day`
    : scheduleSummary.frequency
  const assetStatus = campaign.assetStatus
    ?? campaign.performance.videoMetrics.find((metric) => metric.key === 'asset_status')?.value
    ?? campaign.status

  return (
    <div className="grid md:grid-cols-2 gap-4 items-start">
      <div className="flex flex-col gap-4">
        <div className="rounded-2xl border border-border-lines p-4">
          <p className="text-xs font-semibold text-secondary tracking-wide uppercase mb-3">{f.video_asset}</p>

          <div className="w-full aspect-video rounded-xl bg-dark-blue flex items-center justify-center mb-3 overflow-hidden">
            {
              campaign.thumbnailUrl
                ? <img src={campaign.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                : <UIcons.UilPlayCircle size={48} className="text-white opacity-80" />
            }
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-dark-blue truncate">{campaign.video.name}</p>
              <p className="text-xs text-secondary">{campaign.video.format} • {campaign.video.duration} • {campaign.video.size}</p>
            </div>

            {
              campaign.mediaUrl
                ? (
                  <a
                    href={campaign.mediaUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 border border-border-lines rounded-full h-9 px-3.5 text-sm font-medium text-dark-blue whitespace-nowrap"
                  >
                    <UIcons.UilImport size={15} /> {f.download}
                  </a>
                )
                : (
                  <button
                    type="button"
                    className="flex items-center gap-1.5 border border-border-lines rounded-full h-9 px-3.5 text-sm font-medium text-dark-blue whitespace-nowrap"
                  >
                    <UIcons.UilImport size={15} /> {f.download}
                  </button>
                )
            }
          </div>
        </div>

        <Card title={f.repeat_settings_title}>
          <Row icon={UIcons.UilPlayCircle} label={f.days_to_display}>
            <TagList value={scheduleSummary.daysToDisplay} />
          </Row>
          <Row icon={UIcons.UilClockThree} label={f.hours_to_repeat}>{scheduleSummary.hoursToDisplay}</Row>
          <Row icon={UIcons.UilCalendarAlt} label={f.days_to_repeat_campaign}>{`${scheduleSummary.daysToDisplay} (${campaign.period.label})`}</Row>
          <Row icon={UIcons.UilInfoCircle} label={f.delivery_note}>{deliveryNote}</Row>
        </Card>
      </div>

      <div className="flex flex-col gap-4">
        <Card title={f.creative_detail_title}>
          <Row icon={UIcons.UilInfoCircle} label={f.title}>{campaign.title}</Row>
          <Row icon={UIcons.UilLocationArrow} label={f.cta_message}>{campaign.ctaMessage}</Row>
          <Row icon={UIcons.UilInfoCircle} label={f.description}>{campaign.description}</Row>
          <Row icon={UIcons.UilLink} label={f.callback_url}>{campaign.callbackUrl}</Row>
          <Row icon={UIcons.UilSync} label={f.asset_status}>
            <span className={`font-medium ${campaign.blocked ? 'text-danger' : 'text-success'}`}>{assetStatus}</span>
          </Row>
        </Card>

        <Card title={f.schedule_title}>
          <SplitRow
            icon={UIcons.UilWallet}
            leftLabel={f.budget} leftValue={`NGN ${Number(campaign.budget).toLocaleString()}`}
            rightLabel={f.daily_budget} rightValue={`NGN ${Number(campaign.dailyBudget).toLocaleString()}`}
          />
          <SplitRow
            icon={UIcons.UilCalendarAlt}
            leftLabel={f.start_date} leftValue={scheduleSummary.startDate}
            rightLabel={f.end_date} rightValue={scheduleSummary.endDate}
          />
          <Row icon={UIcons.UilClockThree} label={f.hours_to_display}>{scheduleSummary.hoursToDisplay}</Row>
          <Row icon={UIcons.UilSync} label={f.frequency}>{scheduleSummary.frequency}</Row>
        </Card>
      </div>
    </div>
  )
})
